/*
 * NWF-179 (m13): show/hide the mobile search bar based on scroll direction.
 *
 * header.js already has a scroll listener (StickyHeader) that drives the
 * desktop mega-menu collapse via `is-hide-nav` on <body>, but that class only
 * flips at the header boundary - it hides once you scroll past the header
 * and only comes back once you scroll all the way back above it, regardless
 * of direction in between. That's the wrong shape for the search bar: it
 * should reappear on ANY upward scroll, not just at the very top. So this
 * runs its own small, independent direction tracker instead of reusing
 * is-hide-nav, and drives a separate class (nwf-search-hidden) that
 * nwf-overwrite.css hooks into - only under its mobile media query, so this
 * is a no-op visually above that breakpoint.
 *
 * The tracker has to be immune to its own side effects. `.header-sticky` is
 * `position: sticky` and therefore still in normal flow, so collapsing the
 * search row shrinks the document by the row's height. The browser then
 * corrects the scroll offset for that (scroll anchoring, and plain clamping
 * when the shorter document no longer reaches the current offset), which
 * arrives here as an ordinary scroll event with a delta of up to the row's
 * full height - indistinguishable from the user scrolling. Reacting to it
 * re-expands the bar, which grows the document, which produces the opposite
 * correction, which collapses it again: the bar flickers open/closed
 * indefinitely, including after the user has stopped scrolling. Three rules
 * below break that loop, and none of them may be relaxed on its own:
 *
 *   1. The "near the top" threshold is a cached expanded height, never the
 *      live `offsetHeight` - that shrinks with the very state it gates, so a
 *      live read makes the threshold chase its own output.
 *   2. Flipping requires accumulated travel in one direction, not a single
 *      delta, so no single scroll correction is large enough to flip it.
 *   3. After every flip the tracker ignores direction for the length of the
 *      CSS transition and keeps re-syncing its reference point, so the
 *      corrections caused by that flip are absorbed instead of acted on.
 */
(function () {
    'use strict';

    var HIDE_CLASS = 'nwf-search-hidden';

    // Uninterrupted travel in one direction before the bar flips. Deliberately
    // larger than the search row's own height (8rem), which is the largest
    // scroll correction collapsing/expanding it can produce - so a correction
    // can never on its own reach the threshold.
    var DIRECTION_THRESHOLD = 40;

    // Slightly longer than --header-duration (0.25s) in nwf-overwrite.css: the
    // window in which the document is still resizing after a flip, and scroll
    // events therefore say nothing about what the user is doing.
    var SETTLE_MS = 350;

    var lastScrollY = window.scrollY;
    var travel = 0;
    var settleUntil = 0;
    var expandedHeight = 0;
    var isHidden = false;
    var ticking = false;

    function header() {
        return document.querySelector('.header[data-collapse-on-scroll="true"]');
    }

    function setHidden(next) {
        if (next === isHidden) return;

        isHidden = next;
        document.body.classList.toggle(HIDE_CLASS, next);
        travel = 0;
        settleUntil = Date.now() + SETTLE_MS;
    }

    function update() {
        ticking = false;

        var headerEl = header();
        if (!headerEl) return;

        var scrollY = window.scrollY;
        var delta = scrollY - lastScrollY;
        lastScrollY = scrollY;

        if (Date.now() < settleUntil) {
            // Mid-flip: the document is still resizing, so this delta is the
            // browser correcting for that, not the user. lastScrollY has
            // already absorbed it above; drop the accumulator too so the
            // correction cannot spill into the next decision.
            travel = 0;
            return;
        }

        // Only measured while settled and expanded, i.e. never mid-transition,
        // so this stays the full height regardless of the current state.
        if (!isHidden) expandedHeight = headerEl.offsetHeight;

        if (scrollY <= expandedHeight) {
            // Always visible near the top, independent of direction - also
            // covers iOS overscroll bounce reporting a spurious downward
            // delta right at scrollY 0.
            setHidden(false);
            return;
        }

        if (delta === 0) return;
        if (delta > 0 !== travel > 0) travel = 0;
        travel += delta;

        if (travel > DIRECTION_THRESHOLD) {
            setHidden(true);
        } else if (travel < -DIRECTION_THRESHOLD) {
            setHidden(false);
        }
    }

    function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
    }

    function init() {
        var headerEl = header();
        if (headerEl) expandedHeight = headerEl.offsetHeight;

        window.addEventListener('scroll', onScroll, { passive: true });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
